import React, { useState, useEffect, useMemo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultService, Player, RatingHistory, LeagueOutput } from '../generated-api';
import { getRatingHistories } from '../services/ratingHistories';
import PlayerList from '../components/common/PlayerList';
import PageHeader from '../components/common/PageHeader';
import FilterButton from '../components/common/FilterButton';
import SearchButton from '../components/common/SearchButton';
import PlayerFilterWidget from '../components/common/PlayerFilterWidget';
import { useLeagues } from '../contexts/DataContext';

const PlayerListPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const { leagues, fetch: fetchLeagues } = useLeagues();
  const [playerHistories, setPlayerHistories] = useState<Record<string, RatingHistory>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [minPlayCount, setMinPlayCount] = useState<string>('');
  const [maxPlayCount, setMaxPlayCount] = useState<string>('');
  const [excludeLowConfidence, setExcludeLowConfidence] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const loadPlayers = async () => {
    try {
      setError(null);
      const [playersData, leaguesData] = await Promise.all([
        DefaultService.getPlayersPlayersGet(),
        fetchLeagues() // Use context-provided fetch
      ]);
      setPlayers(playersData);
      
      // Find the global league to get rating histories
      const globalLeague = leaguesData.find(league => league.isGlobal);
      
      // Load rating histories for all players (using global league)
      if (globalLeague) {
        try {
          setPlayerHistories(await getRatingHistories(globalLeague.id));
        } catch (err) {
          console.error('Failed to load rating histories:', err);
        }
      }
    } catch (err) {
      setError('Failed to load players. Please try again.');
      console.error('Error loading players:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a mapping of playerId -> leagues they belong to
  const playerLeaguesMap = useMemo(() => {
    const map: Record<string, LeagueOutput[]> = {};
    if (!leagues) return map;
    
    leagues.forEach(league => {
      league.playerIds?.forEach(playerId => {
        if (!map[playerId]) {
          map[playerId] = [];
        }
        map[playerId].push(league);
      });
    });
    
    return map;
  }, [leagues]);

  // Get play count for a player
  const getPlayCountForPlayer = (playerId: string): number => {
    const history = playerHistories[playerId];
    return history?.ratingProgression?.length || 0;
  };

  // Check if player has low confidence rating (fewer than 5 games or sigma > 5)
  const isPlayerRatingUncertain = (playerId: string): boolean => {
    const history = playerHistories[playerId];
    const gamesPlayed = history?.ratingProgression?.length || 0;
    if (gamesPlayed < 5) return true;
    // Get the latest sigma
    if (history?.ratingProgression && history.ratingProgression.length > 0) {
      const lastEntry = history.ratingProgression[history.ratingProgression.length - 1];
      if (lastEntry.ratingAfter.sigma > 5) return true;
    }
    return false;
  };

  // Filter players based on selected filters
  const filteredPlayers = useMemo(() => {
    return players.filter(player => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (!player.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Filter by league
      if (selectedLeague) {
        const playerLeagues = playerLeaguesMap[player.id] || [];
        const isInLeague = playerLeagues.some(league => league.id === selectedLeague);
        if (!isInLeague) {
          return false;
        }
      }

      // Filter by play count range
      const playCount = getPlayCountForPlayer(player.id);
      if (minPlayCount) {
        const min = parseInt(minPlayCount, 10);
        if (!isNaN(min) && playCount < min) {
          return false;
        }
      }
      if (maxPlayCount) {
        const max = parseInt(maxPlayCount, 10);
        if (!isNaN(max) && playCount > max) {
          return false;
        }
      }

      // Filter by low confidence
      if (excludeLowConfidence && isPlayerRatingUncertain(player.id)) {
        return false;
      }

      return true;
    });
  // getPlayCountForPlayer and isPlayerRatingUncertain depend on playerHistories which is already listed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, selectedLeague, minPlayCount, maxPlayCount, excludeLowConfidence, playerLeaguesMap, playerHistories, searchQuery]);

  const handleClearFilters = () => {
    setSelectedLeague('');
    setMinPlayCount('');
    setMaxPlayCount('');
    setExcludeLowConfidence(false);
  };

  useEffect(() => {
    loadPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]); // Refresh when location changes (navigating to this page)
  
  // Also refresh when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadPlayers();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - loadPlayers is stable

  const handleDeletePlayer = async (playerId: string, playerName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${playerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await DefaultService.deletePlayerPlayersPlayerIdDelete({ playerId: playerId });
      setPlayers(players.filter(p => p.id !== playerId));
    } catch (err) {
      setError('Failed to delete player. Please try again.');
      console.error('Error deleting player:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-fluid-h1 font-bold text-heading">Players</h1>
          <p className="mt-2 text-body">
            {searchQuery ? (
              <>Showing {filteredPlayers.length} of {players.length} players</>
            ) : (
              <>Manage players and view their TrueSkill ratings</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton
            isExpanded={searchExpanded}
            onToggle={() => setSearchExpanded(!searchExpanded)}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search players..."
          />
          <FilterButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            isExpanded={isFilterExpanded}
            hasActiveFilters={!!(selectedLeague || minPlayCount || maxPlayCount || excludeLowConfidence)}
          />
          <button
            data-track="player_new"
            onClick={() => navigate('/players/new')}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            aria-label="Add player"
          >
            <PlusIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Player</span>
          </button>
        </div>
      </PageHeader>
      <PlayerFilterWidget
        players={players}
        leagues={leagues || []}
        playerHistories={playerHistories}
        selectedLeague={selectedLeague}
        minPlayCount={minPlayCount}
        maxPlayCount={maxPlayCount}
        excludeLowConfidence={excludeLowConfidence}
        onSelectedLeagueChange={setSelectedLeague}
        onMinPlayCountChange={setMinPlayCount}
        onMaxPlayCountChange={setMaxPlayCount}
        onExcludeLowConfidenceChange={setExcludeLowConfidence}
        onClearFilters={handleClearFilters}
        filteredPlayersCount={filteredPlayers.length}
        isExpanded={isFilterExpanded}
        onToggleExpanded={() => setIsFilterExpanded(!isFilterExpanded)}
      />

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Players List */}
      <PlayerList 
        players={filteredPlayers}
        playerHistories={playerHistories}
        playerLeaguesMap={playerLeaguesMap}
        showActions={true}
        showCreateButton={true}
        showRanking={true}
        onCreatePlayer={() => navigate('/players/new')}
        onDeletePlayer={handleDeletePlayer}
      />
    </div>
  );
};

export default PlayerListPage;
